<template>
  <section class="mt-7" data-testid="cockpit-commitment-quality">
    <div
      class="bg-white rounded-[12px] p-5 shadow-sm border border-[#E5E5E5]"
    >
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <i class="pi pi-shield text-[0.9rem] text-[var(--color-disabled)]" aria-hidden="true" />
        <p class="text-[13px] font-medium text-[var(--color-dark)] m-0">
          {{ $t('UserProjects.cockpit.commitmentQuality.title') }}
        </p>
        <span class="text-[11px] text-[var(--color-disabled)] ml-auto">
          {{ $t('UserProjects.cockpit.commitmentQuality.subtitle') }}
        </span>
      </div>

      <div v-if="loading" class="text-[13px] text-[var(--color-disabled)] py-4">
        {{ $t('UserProjects.cockpit.commitmentQuality.loading') }}
      </div>

      <div v-else-if="error" class="text-[13px] text-[var(--color-error,#EF4444)] py-2">
        {{ error }}
      </div>

      <template v-else>
        <div
          v-if="insufficientData"
          class="text-[13px] text-[var(--color-disabled)] mb-4"
          data-testid="cockpit-cq-insufficient"
        >
          {{ $t('UserProjects.cockpit.commitmentQuality.insufficient') }}
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div
            class="rounded-[10px] p-3 bg-[#F8F8F8] border border-[#F0F0F0]"
            data-testid="cockpit-cq-reliability"
          >
            <p class="text-[10px] uppercase tracking-wider text-[var(--color-disabled)] mb-1 m-0">
              {{ $t('UserProjects.cockpit.commitmentQuality.reliability') }}
            </p>
            <p class="text-[24px] font-bold text-[var(--color-dark)] leading-tight m-0">
              {{ formatPercent(reliabilityPercent) }}
              <span class="text-[14px] text-[var(--color-disabled)]">%</span>
            </p>
            <p class="text-[11px] text-[var(--color-disabled)] mt-0.5 mb-0">
              {{ $t('UserProjects.cockpit.commitmentQuality.reliabilityHint') }}
            </p>
          </div>
          <div
            class="rounded-[10px] p-3 bg-[#F8F8F8] border border-[#F0F0F0]"
            data-testid="cockpit-cq-ownership"
          >
            <p class="text-[10px] uppercase tracking-wider text-[var(--color-disabled)] mb-1 m-0">
              {{ $t('UserProjects.cockpit.commitmentQuality.ownership') }}
            </p>
            <p class="text-[24px] font-bold text-[var(--color-dark)] leading-tight m-0">
              {{ formatPercent(ownershipPercent) }}
              <span class="text-[14px] text-[var(--color-disabled)]">%</span>
            </p>
            <p class="text-[11px] text-[var(--color-disabled)] mt-0.5 mb-0">
              {{ $t('UserProjects.cockpit.commitmentQuality.ownershipHint') }}
            </p>
          </div>
        </div>

        <div class="space-y-2.5 pt-3 border-t border-[#F0F0F0]">
          <p class="text-[11px] uppercase tracking-wider text-[var(--color-disabled)] font-medium mb-1 m-0">
            {{ $t('UserProjects.cockpit.commitmentQuality.detailTitle') }}
          </p>
          <div
            v-for="metric in metrics"
            :key="metric.key"
            class="flex items-center gap-3"
            :data-testid="`cockpit-cq-metric-${metric.key}`"
          >
            <span class="text-[12px] text-[var(--color-dark)] flex-1 min-w-0">
              {{ metricLabel(metric.key) }}
            </span>
            <div class="flex-1 h-2 rounded-full bg-[#F0F0F0] overflow-hidden max-w-[100px]">
              <div
                class="h-full rounded-full"
                :style="{
                  width: `${metric.percent ?? 0}%`,
                  backgroundColor: barColor(metric.colorHint),
                }"
              />
            </div>
            <span class="text-[12px] font-bold text-[var(--color-dark)] w-8 text-right">
              {{ formatPercent(metric.percent) }}%
            </span>
          </div>
          <div class="flex items-start gap-1.5 mt-2 text-[11px] text-[var(--color-disabled)]">
            <i class="pi pi-info-circle text-[0.75rem] mt-0.5 shrink-0" aria-hidden="true" />
            <span>{{ $t('UserProjects.cockpit.commitmentQuality.disclaimer') }}</span>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CockpitCommitmentQualitySection',
  props: {
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    insufficientData: { type: Boolean, default: false },
    reliabilityPercent: { type: Number, default: null },
    ownershipPercent: { type: Number, default: null },
    metrics: { type: Array, default: () => [] },
  },
  methods: {
    formatPercent(value) {
      return typeof value === 'number' ? String(value) : '—';
    },
    metricLabel(key) {
      return this.$t(`UserProjects.cockpit.commitmentQuality.metrics.${key}`);
    },
    barColor(hint) {
      if (hint === 'green') return '#22C55E';
      if (hint === 'red') return '#EF4444';
      return '#EAB308';
    },
  },
};
</script>
